import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";

import useApi from "../../../../api";
import useHelpers from "../../../../helpers";

const useSettingsCreate = () => {
  const { newId } = useParams();
  const navigation = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [showValue, setShowValue] = useState(false);
  const [contentType, setContentType] = useState("string");
  // Edición de secretos: el valor real nunca llega del backend (viene
  // enmascarado), así que por defecto se CONSERVA. El usuario debe pedir
  // explícitamente reemplazarlo para introducir un valor nuevo.
  const [replaceSecret, setReplaceSecret] = useState(false);
  const [maskedContent, setMaskedContent] = useState("");

  const { useActions } = useApi();
  const { useSettingsActions } = useActions();
  const { actGetSetting, actCreateSetting, actUpdateSetting } = useSettingsActions();

  const { useQuickFunctions, useSettingsSecrets } = useHelpers();
  const { useToast } = useQuickFunctions();
  const { success, error: errorToast } = useToast();
  const { isSecretKey } = useSettingsSecrets();

  const settingValidationSchema = yup.object().shape({
    id: yup.string().required("El ID es requerido"),
    content: yup.string().required("El contenido es requerido"),
  });

  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    mode: "all",
    resolver: yupResolver(settingValidationSchema),
    defaultValues: {
      id: "",
      content: "",
    }
  });

  const watchedId = watch("id");
  const isSensitiveKey = isSecretKey(watchedId);

  // ¿Estamos editando un secreto que aún se conserva (sin reemplazar)?
  const isPreservingSecret = isEditMode && isSensitiveKey && !replaceSecret;

  const detectContentType = (value) => {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        return "object";
      } else if (typeof parsed === "number") {
        return "number";
      } else if (typeof parsed === "boolean") {
        return "boolean";
      }
      return "string";
    } catch {
      return "string";
    }
  };

  const onSubmit = (data) => {
    // Conservar credencial: al editar un secreto sin reemplazarlo, no se envía
    // el content (el valor real nunca salió del backend). Garantiza que no se
    // sobrescriba, sin depender de reenviar la máscara.
    if (isPreservingSecret) {
      actUpdateSetting(newId, {}, onUpdateSuccess, onError);
      return;
    }

    let contentToSend;

    try {
      if (contentType === "object") {
        JSON.parse(data.content);
        contentToSend = data.content;
      } else if (contentType === "number") {
        contentToSend = JSON.stringify(Number(data.content));
      } else if (contentType === "boolean") {
        contentToSend = JSON.stringify(data.content === "true" || data.content === true);
      } else {
        contentToSend = JSON.stringify(data.content);
      }
    } catch (e) {
      errorToast("Error en el formato del contenido. Verifica que sea un JSON válido.");
      return;
    }

    const payload = {
      id: data.id,
      content: contentToSend
    };

    if (isEditMode) {
      actUpdateSetting(newId, { content: contentToSend }, onUpdateSuccess, onError);
    } else {
      actCreateSetting(payload, onCreateSuccess, onError);
    }
  };

  const handleReplaceSecret = () => {
    setReplaceSecret(true);
    setShowValue(false);
    setValue("content", "");
  };

  const handleCancelReplaceSecret = () => {
    setReplaceSecret(false);
    setShowValue(false);
    setValue("content", maskedContent);
  };

  const onCreateSuccess = () => {
    success("Configuración creada con éxito!");
    setTimeout(() => {
      navigation("/configuracion/settings");
    }, 1000);
  };

  const onUpdateSuccess = () => {
    success("Configuración actualizada con éxito!");
    setTimeout(() => {
      navigation("/configuracion/settings");
    }, 1000);
  };

  const onError = (error) => {
    const message = error?.response?.data?.message || "Error al guardar la configuración";
    errorToast(message);
  };

  const loadSetting = (data) => {
    const { id, content } = data.data;
    setValue("id", id);

    // Secreto: el backend devuelve el valor enmascarado (••••1234). Se conserva
    // por defecto; el campo mantiene la máscara hasta que el usuario reemplace.
    if (isSecretKey(id)) {
      setContentType("string");
      setMaskedContent(content);
      setValue("content", content);
      return;
    }

    try {
      const parsed = JSON.parse(content);
      const type = detectContentType(content);
      setContentType(type);
      if (type === "object") {
        setValue("content", JSON.stringify(parsed, null, 2));
      } else {
        setValue("content", String(parsed));
      }
    } catch {
      setValue("content", content);
    }
  };

  useEffect(() => {
    if (newId) {
      setIsEditMode(true);
      actGetSetting(newId, loadSetting, onError);
    }
  }, [newId]);

  return {
    control,
    handleSubmit,
    onSubmit,
    isEditMode,
    errors,
    showValue,
    setShowValue,
    isSensitiveKey,
    contentType,
    setContentType,
    isPreservingSecret,
    replaceSecret,
    handleReplaceSecret,
    handleCancelReplaceSecret,
  };
};

export default useSettingsCreate;
